import type {
  ContentObject,
  ContentPreviewConfigEntry,
} from '../../../lib/flotiq-client/types/api-response';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import { usePublicVersion } from '../../../hooks/usePublicVersion';

import { PreviewIcon, WorldIcon } from '../../../images/shapes';

import Button from '../../Button/Button';
import LinkButton from '../../LinkButton/LinkButton';
import Loader from '../../Loader/Loader';
import { URLGenerator } from '../helpers/UrlGenerator';

interface PreviewLinksProps {
  /**
   * A single content preview config entry
   */
  config: ContentPreviewConfigEntry;
  /**
   * Base URL of the client site that serves the preview
   */
  baseUrl?: string;
  /**
   * Editor key used to authorize the draft preview on the client site
   */
  editorKey?: string;
  /**
   * The content object currently being edited
   */
  contentObject?: ContentObject | null;
  /**
   * Whether a new object is being created
   */
  create?: boolean;
  /**
   * Whether the form has unsaved changes
   */
  isDirty?: boolean;
  /**
   * Whether the content type uses the draft/public publishing workflow
   */
  draftPublic?: boolean;
  /**
   * Saves the form from the panel; resolves with the saved object or null
   * when the save failed
   */
  onPanelSave?: () => Promise<ContentObject | null>;
  /**
   * Test id for the component
   */
  testId?: string;
}

const openInTab = (() => {
  let tabRef: Window | null = null;
  return (url: string) => {
    if (tabRef && !tabRef.closed) {
      tabRef.location.replace(url);
      tabRef.focus();
      return;
    }
    tabRef = window.open(url, '_blank');
    tabRef?.focus();
  };
})();

const PreviewLinks = ({
  config,
  baseUrl,
  editorKey,
  contentObject,
  create,
  isDirty,
  draftPublic,
  onPanelSave,
  testId,
}: PreviewLinksProps) => {
  const { t } = useTranslation();
  const urlGenerator = useMemo(
    () =>
      new URLGenerator({
        baseUrl,
        editorKey,
        routeTemplate: config.route_template,
      }),
    [baseUrl, config.route_template, editorKey],
  );

  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const hasPublicLink = !!draftPublic;
  const mustSave = create || isDirty;

  const publicVersion = usePublicVersion(
    hasPublicLink ? contentObject : undefined,
  );

  const publicHref = useMemo(() => {
    if (!publicVersion) return '';
    try {
      return urlGenerator
        .getURL(publicVersion as unknown as Record<string, unknown>, false)
        .toString();
    } catch {
      return '';
    }
  }, [publicVersion, urlGenerator]);

  const isPublicDisabled = !publicHref;

  const previewKey = hasPublicLink
    ? 'ContentPreview.Preview'
    : 'ContentPreview.View';
  const saveKey = hasPublicLink
    ? 'ContentPreview.PreviewAndSave'
    : 'ContentPreview.SaveAndView';

  const previewLabel = t(mustSave ? saveKey : previewKey);

  const onPreviewClick = useCallback(async () => {
    let source: Record<string, unknown> | undefined;

    if (mustSave) {
      if (!onPanelSave) return;
      setIsPreviewLoading(true);
      const saved = await onPanelSave();
      setIsPreviewLoading(false);
      if (!saved) return;
      source = saved as Record<string, unknown>;
    } else {
      source = (contentObject ?? {}) as Record<string, unknown>;
    }

    try {
      openInTab(urlGenerator.getURL(source, hasPublicLink).toString());
    } catch {
      // invalid URL (e.g. missing base_url) — nothing to open
    }
  }, [mustSave, onPanelSave, contentObject, urlGenerator, hasPublicLink]);

  return (
    <div
      className={twMerge(
        hasPublicLink && 'justify-center',
        'flex flex-wrap gap-x-6 gap-y-4 3xl:justify-start 3xl:flex-nowrap 3xl:gap-4',
      )}
      {...getTestProps(testId, 'preview-item')}
    >
      <Button
        iconImage={
          isPreviewLoading ? (
            <Loader
              size="big"
              type="spinner-grid"
              {...getTestProps(testId, 'loading', 'testId')}
            />
          ) : (
            <PreviewIcon className="w-3.5" />
          )
        }
        iconPosition={'start'}
        onClick={onPreviewClick}
        disabled={isPreviewLoading}
        buttonSize={'md'}
        buttonColor={'blueBordered'}
        additionalClasses={twMerge(
          'flex justify-center w-full max-w-[180px] h-9',
          'font-medium text-sm text-blue',
          '3xl:text-base 3xl:max-w-[210px]',
        )}
        {...getTestProps(testId, 'preview-link', 'testId')}
      >
        <span>{previewLabel}</span>
      </Button>

      {hasPublicLink && (
        <LinkButton
          iconImage={<WorldIcon className="w-4" />}
          iconPosition={'start'}
          link={publicHref}
          disabled={isPublicDisabled}
          target="_blank"
          rel="noreferrer"
          buttonSize={'md'}
          buttonColor={'borderless'}
          additionalClasses={twMerge(
            'w-[180px] justify-center max-w-full',
            'font-medium text-sm underline text-blue',
            '3xl:w-auto 3xl:text-base',
            'hover:text-blue-600 dark:hover:text-blue focus:!text-blue',
            isPublicDisabled &&
              '!bg-transparent !text-blue opacity-50 dark:!text-slate-200',
          )}
          {...getTestProps(testId, 'public-link', 'testId')}
        >
          <span>{t('ContentPreview.PublicVersion')}</span>
        </LinkButton>
      )}
    </div>
  );
};

export default PreviewLinks;
